/**
 * Agent Context - Manages selected agent state across the application.
 *
 * Provides:
 * - List of available agents from orchestrator
 * - Currently selected agent
 * - Agent selection handler
 * - Loading/error states
 *
 * Blueprint: specs/021-multi-agent-orchestrator/tasks.md (T021)
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  orchestratorApi,
  type AgentSummary,
  type AgentListResponse,
} from '../services/orchestratorApi';

export interface AgentContextValue {
  /** List of available agents */
  agents: AgentSummary[];
  /** Currently selected agent */
  selectedAgent: AgentSummary | null;
  /** The default agent slug from orchestrator */
  defaultAgentSlug: string | null;
  /** SSP persona slug used when Mystique is selected (ignored otherwise) */
  mystiqueSlug: string;
  /** Set (and persist) the Mystique persona slug */
  setMystiqueSlug: (slug: string) => void;
  /**
   * Slug to use in orchestrator URLs (`/agents/{slug}/chat`, `/agents/{slug}/ws/...`).
   *
   * Same as the selected agent's slug, except for Mystique, where the persona
   * is carried in the slug itself: `mystique_{persona}` (e.g. `mystique_test-bot`).
   */
  routingSlug: string | null;
  /** Whether agents are being loaded */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Select an agent by slug */
  selectAgent: (slug: string) => void;
  /** Refresh agent list from orchestrator */
  refreshAgents: () => Promise<void>;
  /** Get chat URL for selected agent */
  getChatUrl: () => string;
  /** Get WebSocket URL for selected agent */
  getWebSocketUrl: (sessionId: string) => string;
}

const AgentContext = createContext<AgentContextValue | null>(null);

const AGENT_SELECTION_KEY = 'cxs:selected-agent';
const MYSTIQUE_SLUG_KEY = 'cxs:mystique:slug';
/** Registry slug of the Mystique engine; personas route as `mystique_{persona}`. */
const MYSTIQUE_AGENT_SLUG = 'mystique';
const DEFAULT_MYSTIQUE_SLUG = 'test-bot';

function getPersistedAgentSlug(): string | null {
  try {
    return localStorage.getItem(AGENT_SELECTION_KEY);
  } catch {
    return null;
  }
}

function persistAgentSlug(slug: string): void {
  try {
    localStorage.setItem(AGENT_SELECTION_KEY, slug);
  } catch {
    // ignore
  }
}

function getPersistedMystiqueSlug(): string {
  try {
    return localStorage.getItem(MYSTIQUE_SLUG_KEY) || DEFAULT_MYSTIQUE_SLUG;
  } catch {
    return DEFAULT_MYSTIQUE_SLUG;
  }
}

export interface AgentProviderProps {
  children: React.ReactNode;
  /** Initial agent slug to select (overrides default) */
  initialAgentSlug?: string;
}

/**
 * Agent Provider - manages agent discovery and selection.
 *
 * On mount, fetches available agents from orchestrator and selects
 * either the initial agent (if provided) or the default agent.
 */
export function AgentProvider({ children, initialAgentSlug }: AgentProviderProps) {
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentSummary | null>(null);
  const [defaultAgentSlug, setDefaultAgentSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mystiqueSlug, setMystiqueSlugState] = useState(getPersistedMystiqueSlug);

  const setMystiqueSlug = useCallback((slug: string) => {
    setMystiqueSlugState(slug);
    try {
      localStorage.setItem(MYSTIQUE_SLUG_KEY, slug);
    } catch {
      // ignore
    }
  }, []);

  // Mystique carries the persona in the URL slug: /agents/mystique_{persona}/…
  const routingSlug = useMemo(() => {
    const slug = selectedAgent?.slug ?? null;
    const persona = mystiqueSlug.trim();
    if (slug === MYSTIQUE_AGENT_SLUG && persona) {
      return `${MYSTIQUE_AGENT_SLUG}_${persona}`;
    }
    return slug;
  }, [selectedAgent, mystiqueSlug]);

  // Fetch agents from orchestrator
  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response: AgentListResponse = await orchestratorApi.listAgents();
      setAgents(response.agents);
      setDefaultAgentSlug(response.default_agent);

      // Select initial, previously-persisted, or default agent (in that
      // priority order — the persisted choice survives a page refresh,
      // but an explicit ?agent= URL param always wins).
      const slugToSelect =
        initialAgentSlug || getPersistedAgentSlug() || response.default_agent;
      const agentToSelect = response.agents.find(
        (a) => a.slug === slugToSelect,
      );

      if (agentToSelect) {
        setSelectedAgent(agentToSelect);
      } else if (response.agents.length > 0) {
        // Fallback to first healthy agent
        const healthyAgent = response.agents.find((a) => a.healthy);
        setSelectedAgent(healthyAgent || response.agents[0]);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch agents';
      setError(message);
      console.error('Failed to fetch agents:', err);
    } finally {
      setLoading(false);
    }
  }, [initialAgentSlug]);

  // Initial fetch
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Select agent by slug
  const selectAgent = useCallback(
    (slug: string) => {
      const agent = agents.find((a) => a.slug === slug);
      if (agent) {
        setSelectedAgent(agent);
        persistAgentSlug(agent.slug);
      } else {
        console.warn(`Agent not found: ${slug}`);
      }
    },
    [agents],
  );

  // Refresh agents
  const refreshAgents = useCallback(async () => {
    await fetchAgents();
  }, [fetchAgents]);

  // Get chat URL for selected agent
  const getChatUrl = useCallback(() => {
    if (!routingSlug) {
      return orchestratorApi.getDefaultChatUrl();
    }
    return orchestratorApi.getChatUrl(routingSlug);
  }, [routingSlug]);

  // Get WebSocket URL for selected agent
  const getWebSocketUrl = useCallback(
    (sessionId: string) => {
      // Fall back to default agent when nothing is selected yet
      const slug = routingSlug || defaultAgentSlug || 'dex';
      return orchestratorApi.getWebSocketUrl(slug, sessionId);
    },
    [routingSlug, defaultAgentSlug],
  );

  const value = useMemo<AgentContextValue>(
    () => ({
      agents,
      selectedAgent,
      defaultAgentSlug,
      mystiqueSlug,
      setMystiqueSlug,
      routingSlug,
      loading,
      error,
      selectAgent,
      refreshAgents,
      getChatUrl,
      getWebSocketUrl,
    }),
    [
      agents,
      selectedAgent,
      defaultAgentSlug,
      mystiqueSlug,
      setMystiqueSlug,
      routingSlug,
      loading,
      error,
      selectAgent,
      refreshAgents,
      getChatUrl,
      getWebSocketUrl,
    ],
  );

  return (
    <AgentContext.Provider value={value}>{children}</AgentContext.Provider>
  );
}

/**
 * Hook to access agent context.
 *
 * Must be used within an AgentProvider.
 */
export function useAgent(): AgentContextValue {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgent must be used within an AgentProvider');
  }
  return context;
}

/**
 * Hook to access agent context (optional).
 *
 * Returns null if not within an AgentProvider.
 */
export function useAgentOptional(): AgentContextValue | null {
  return useContext(AgentContext);
}
